import { PARTS, AnyPartData } from '../parts/PartRegistry.js';
import { PartTag } from '../parts/Part.js';

/**
 * Explicit fusion table for tier-1 fusions.
 *
 * Maps part A -> part B -> result part ID.
 * This table is bidirectional - both orderings are supported.
 */
const FUSION_TABLE: Record<string, Record<string, string>> = {
  // ========== HEADS ==========
  scanner: {
    tracker: 'analyst',
    spotter: 'oracle',
  },
  tracker: {
    scanner: 'analyst',
    spotter: 'predator',
  },
  spotter: {
    scanner: 'oracle',
    tracker: 'predator',
  },

  // ========== WEAPONS ==========
  // Original 3 weapons
  repeater: {
    lancer: 'railgun',
    sprayer: 'storm',
    seeker: 'hunter',
    splitter: 'cluster',
    mortar: 'bombardier',
  },
  lancer: {
    repeater: 'railgun',
    sprayer: 'nova',
    splitter: 'hydra',
    needler: 'railneedle',
  },
  sprayer: {
    repeater: 'storm',
    lancer: 'nova',
    ricochet: 'chaos',
    needler: 'shredder',
  },
  // New behavior weapons
  seeker: {
    repeater: 'hunter',
    ricochet: 'pinball',
    mortar: 'smart_bomb',
    shocker: 'arc_hunter',
  },
  ricochet: {
    sprayer: 'chaos',
    seeker: 'pinball',
  },
  splitter: {
    repeater: 'cluster',
    lancer: 'hydra',
    mortar: 'cluster_bomb',
    shocker: 'storm_caller',
  },
  // Mortar - arcing AOE
  mortar: {
    repeater: 'bombardier',
    seeker: 'smart_bomb',
    splitter: 'cluster_bomb',
  },
  // Needler - fast piercing
  needler: {
    lancer: 'railneedle',
    sprayer: 'shredder',
  },
  // Shocker - chain lightning
  shocker: {
    seeker: 'arc_hunter',
    splitter: 'storm_caller',
  },

  // ========== CORES ==========
  standard: {
    capacitor: 'dynamo',
    bulwark: 'fortress',
  },
  capacitor: {
    standard: 'dynamo',
    bulwark: 'reactor',
  },
  bulwark: {
    standard: 'fortress',
    capacitor: 'reactor',
  },

  // ========== TRANSPORTS ==========
  treads: {
    hover: 'glider',
    legs: 'crawler',
  },
  hover: {
    treads: 'glider',
    legs: 'strider',
  },
  legs: {
    treads: 'crawler',
    hover: 'strider',
  },
};

/**
 * Get the fusion result of two parts.
 *
 * First checks the explicit fusion table for a defined combination.
 * If no explicit match exists, falls back to tag-based fusion algebra.
 *
 * @param partIdA - ID of first part
 * @param partIdB - ID of second part
 * @returns ID of the resulting fused part, or null if fusion fails
 */
export function getFusionResult(partIdA: string, partIdB: string): string | null {
  // Check explicit fusion table (both orderings)
  if (FUSION_TABLE[partIdA]?.[partIdB]) {
    return FUSION_TABLE[partIdA][partIdB];
  }
  if (FUSION_TABLE[partIdB]?.[partIdA]) {
    return FUSION_TABLE[partIdB][partIdA];
  }

  // Fallback to tag algebra for emergent combinations
  return tagAlgebraFusion(partIdA, partIdB);
}

/**
 * Tag-based fusion fallback system.
 *
 * Combines the tags of both input parts and finds the best matching part.
 * This allows for emergent fusion combinations not explicitly defined.
 *
 * Algorithm:
 * 1. Combine tags from both parts
 * 2. For each candidate part (same slot, higher tier):
 *    - Count how many tags match the combined set
 *    - Track the best match
 * 3. Return the best match if it meets minimum threshold
 *
 * @param partIdA - ID of first part
 * @param partIdB - ID of second part
 * @returns ID of best matching part, or null if no good match found
 */
function tagAlgebraFusion(partIdA: string, partIdB: string): string | null {
  const partA = PARTS[partIdA];
  const partB = PARTS[partIdB];

  if (!partA || !partB) {
    return null;
  }

  // Can only fuse parts from the same slot
  if (partA.slot !== partB.slot) {
    return null;
  }

  // Combine tags from both parts
  const combinedTags = [...partA.tags, ...partB.tags];

  // Find best matching part in the same slot
  let bestMatch: string | null = null;
  let bestScore = 0;

  for (const [candidateId, candidateData] of Object.entries(PARTS)) {
    // Skip if wrong slot
    if (candidateData.slot !== partA.slot) {
      continue;
    }

    // Skip if same as input parts
    if (candidateId === partIdA || candidateId === partIdB) {
      continue;
    }

    // Skip if lower tier (we want fusions to produce higher tier parts)
    if (candidateData.tier <= Math.max(partA.tier, partB.tier)) {
      continue;
    }

    // Calculate match score based on tag overlap
    const score = calculateTagScore(combinedTags, candidateData.tags);

    if (score > bestScore) {
      bestScore = score;
      bestMatch = candidateId;
    }
  }

  // Require minimum score threshold (at least 2 matching tags)
  if (bestScore < 2) {
    return null;
  }

  return bestMatch;
}

/**
 * Calculate how well a candidate's tags match the combined input tags.
 *
 * Uses tag frequency matching - each tag match adds to the score.
 * More common tags in both sets = higher score.
 *
 * @param combinedTags - Tags from both input parts
 * @param candidateTags - Tags from the candidate fusion result
 * @returns Match score (higher = better match)
 */
function calculateTagScore(combinedTags: PartTag[], candidateTags: PartTag[]): number {
  let score = 0;

  // Count frequency of each tag in combined set
  const tagCounts = new Map<PartTag, number>();
  for (const tag of combinedTags) {
    tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
  }

  // For each candidate tag, check if it matches combined tags
  for (const tag of candidateTags) {
    const count = tagCounts.get(tag) || 0;
    if (count > 0) {
      score += count;
      // Decrement count so we don't double-count
      tagCounts.set(tag, count - 1);
    }
  }

  return score;
}
